import * as React from "react";
import {
  Paper,
  CssBaseline,
  Typography,
  makeStyles,
  TextField,
  Grid,
  Checkbox,
  FormHelperText,
  FormControl,
  FormLabel,
} from "@material-ui/core";
import { UserContext } from "../context/UserContext";
import TenantCreate from "../types/TenantCreate";
import { useSnackbar } from "notistack";
import TenantCreateResponse from "../types/TenantCreateResponse";
import { Api } from "../api";
import Recaptcha from "react-recaptcha";
import * as yup from "yup";
import ButtonSpinner from "./ButtonSpinner";
import { useFormik } from "formik";

const SignUpSchema = yup.object({
  tenantName: yup.string().required("Company Name is required."),
  email: yup
    .string()
    .email("Please enter a valid email.")
    .required("Email is required."),
  firstName: yup.string().required("First Name is required."),
  lastName: yup.string().required("Last Name is required."),
  password: yup.string().required("Password is required"),
  verifyPassword: yup
    .string()
    .nullable()
    .oneOf([yup.ref("password"), null], "Passwords do not match.")
    .required("Verify Password is required"),
  tos: yup
    .bool()
    .oneOf(
      [true],
      "Please agree to the terms of service and our privacy policy."
    ),
});

interface IProps {
  onCreateSuccess?: () => void;
}

export default function SignUp(props: IProps) {
  const [formError, setFormError] = React.useState("");
  const [recaptcha, setRecaptcha] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const user = React.useContext(UserContext);
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const formik = useFormik({
    initialValues: {
      tenantName: "",
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      verifyPassword: "",
      tos: false,
    },
    onSubmit: onSubmit,
    validationSchema: SignUpSchema,
  });

  function createAccount(newTenant: TenantCreate) {
    setLoading(true);
    Api.tenants
      .create(newTenant)
      .then((result) => {
        let data: TenantCreateResponse = result.data;
        if (!data.hasError) {
          enqueueSnackbar("Success!", { variant: "success" });
          user.login(newTenant.email, newTenant.password);
        }
      })
      .finally(() => setLoading(false));
  }

  function verifyCallback(response: string) {
    setRecaptcha(response);
  }

  function onRecaptchaLoad() {
    console.log("Recaptcha load");
  }

  function onSubmit(data: any) {
    if (recaptcha.length > 0) {
      var d = data as TenantCreate;
      d.userName = d.email;
      d.recatpcha = recaptcha;
      createAccount(d);
    } else {
      setFormError("Please complete recaptcha.");
    }
  }

  return (
    <Paper className={classes.paper}>
      <CssBaseline />
      <Typography component="h1" variant="h5" className={classes.title}>
        Create Account
      </Typography>
      {formError.length > 0 && <div className={classes.error}>{formError}</div>}
      <form className={classes.form} onSubmit={formik.handleSubmit}>
        <Grid container spacing={3}>
          <Grid item md={12}>
            <TextField
              id="tenantName"
              label="Company Name *"
              variant="outlined"
              name="tenantName"
              fullWidth
              autoComplete="tenantName"
              autoFocus
              error={
                formik.touched.tenantName && formik.errors.tenantName
                  ? true
                  : false
              }
              helperText={
                formik.touched.tenantName && formik.errors.tenantName
                  ? formik.errors.tenantName
                  : null
              }
              onChange={formik.handleChange}
            />
          </Grid>
          <Grid item md={6}>
            <TextField
              id="firstName"
              name="firstName"
              label="First Name *"
              variant="outlined"
              fullWidth
              autoComplete="firstName"
              error={
                formik.touched.firstName && formik.errors.firstName
                  ? true
                  : false
              }
              helperText={
                formik.touched.firstName && formik.errors.firstName
                  ? formik.errors.firstName
                  : null
              }
              onChange={formik.handleChange}
            />
          </Grid>
          <Grid item md={6}>
            <TextField
              id="lastName"
              name="lastName"
              label="Last Name *"
              variant="outlined"
              fullWidth
              autoComplete="lastName"
              error={
                formik.touched.lastName && formik.errors.lastName ? true : false
              }
              helperText={
                formik.touched.lastName && formik.errors.lastName
                  ? formik.errors.lastName
                  : null
              }
              onChange={formik.handleChange}
            />
          </Grid>
          <Grid item md={12}>
            <TextField
              id="email"
              name="email"
              label="Email *"
              variant="outlined"
              fullWidth
              autoComplete="email"
              error={formik.touched.email && formik.errors.email ? true : false}
              helperText={
                formik.touched.email && formik.errors.email
                  ? formik.errors.email
                  : null
              }
              onChange={formik.handleChange}
            />
          </Grid>
          <Grid item md={6}>
            <TextField
              id="password"
              name="password"
              label="Password *"
              variant="outlined"
              fullWidth
              type="password"
              autoComplete="password"
              error={
                formik.touched.password && formik.errors.password ? true : false
              }
              helperText={
                formik.touched.password && formik.errors.password
                  ? formik.errors.password
                  : null
              }
              onChange={formik.handleChange}
            />
          </Grid>
          <Grid item md={6}>
            <TextField
              id="verifyPassword"
              name="verifyPassword"
              label="Verify Password *"
              variant="outlined"
              fullWidth
              type="password"
              autoComplete="verifyPassword"
              error={
                formik.touched.verifyPassword && formik.errors.verifyPassword
                  ? true
                  : false
              }
              helperText={
                formik.touched.verifyPassword && formik.errors.verifyPassword
                  ? formik.errors.verifyPassword
                  : null
              }
              onChange={formik.handleChange}
            />
          </Grid>
          <FormHelperText className={classes.passwordHelper}>
            Must have more than 8 characters, including at least 1 letter and
            number.
          </FormHelperText>
          <Grid item md={12}>
            <FormControl
              error={formik.touched.tos && formik.errors.tos ? true : false}
            >
              <FormLabel className={classes.checkboxLabel}>
                <Checkbox
                  id="tos"
                  name="tos"
                  color="primary"
                  value="true"
                  onChange={formik.handleChange}
                />
                <span>
                  I accept the <a href="//www.google.com">terms of service</a>{" "}
                  and have read the{" "}
                  <a href="//www.google.com">privacy policy</a>
                </span>
              </FormLabel>
              {formik.touched.tos && formik.errors.tos ? (
                <FormHelperText className={classes.tosError}>
                  {formik.errors.tos && formik.errors.tos}
                </FormHelperText>
              ) : null}
            </FormControl>
          </Grid>
          <Grid item md={12}>
            <div className={classes.recaptchaContainer}>
              <Recaptcha
                className={classes.recaptcha}
                sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                render="explicit"
                verifyCallback={verifyCallback}
                onloadCallback={onRecaptchaLoad}
              />
            </div>
          </Grid>
        </Grid>

        <ButtonSpinner
          type="submit"
          fullWidth
          variant="contained"
          color="primary"
          className={classes.button}
          loading={loading}
          disabled={loading}
        >
          Create Account
        </ButtonSpinner>
      </form>
    </Paper>
  );
}

const useStyles = makeStyles((theme) => ({
  form: {
    width: "100%",
  },
  link: {
    color: theme.palette.primary.main,
    textDecoration: "none",
    fontSize: 14,
    fontWeight: 500,
    marginTop: 30,
  },
  main: {
    width: "auto",
    display: "block",
    marginLeft: 15,
    marginRight: 15,
  },
  paper: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: 34,
    flexGrow: 1,
    borderRadius: 20,
  },
  avatar: {
    width: 30,
    height: 40,
    display: "flex",
    position: "relative",
    overflow: "hidden",
    fontSize: "1.25rem",
    alignItems: "center",
    flexShrink: 0,
    userSelect: "none",
    borderRadius: "50%",
    justifyContent: "center",
    backgroundColor: theme.palette.secondary.main,
  },
  button: {
    marginTop: 30,
  },
  title: {
    marginTop: 15,
    marginBottom: 24,
    fontSize: 34,
    fontWeight: 600,
  },
  error: {
    background: "#B00020",
    color: "white",
    padding: 15,
    width: "100%",
    textAlign: "center",
    marginBottom: 32,
  },
  recaptcha: {
    display: "inline-block",
  },
  recaptchaContainer: {
    textAlign: "center",
  },
  checkboxLabel: {
    color: "#5D5D5D",
    fontSize: 14,
    marginLeft: -11,
    marginRight: 16,
    display: "inline-flex",
    lineHeight: 1.5,
  },
  tosError: {
    paddingLeft: 12,
  },
  passwordHelper: {
    paddingLeft: 12,
    paddingRight: 12,
    margin: 0,
  },
}));
